import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { getContractCode, loginFetcher, postContractCode } from './login.fetcher';
import { loginValidationSchema } from './login.schema';
import { Controller, Button } from '../../common';
import './login.scss';
import { ALERT_TYPE, TKeys } from 'utils/enums';
import {
    changeI18nLocale,
    getLocalStorageValue,
    getMessageText,
    setLocalStorageValue,
} from 'utils/helpers';
import { hideLoading, showAlert } from 'reducer/rootReducer';
import { setUser } from 'reducer/userReducer';
import ManagementCode from './management-code';
import ContractCode from './contract-code';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from 'utils/constants';

const MANAGEMENT_CODE = 'SATO0310';
interface IFormInput {
    contractCode?: string;
    companyName?: string;
    account: string;
    password: string;
}
const LoginPage: React.FC = () => {
    const dispatch = useDispatch();
    const contractCode = getLocalStorageValue('contract') ?? '';
    const companyName = getLocalStorageValue('companyName') ?? '';

    const { t } = useTranslation();

    const { control, handleSubmit, setValue } = useForm<IFormInput>({
        resolver: yupResolver(loginValidationSchema(t)),
        defaultValues: {
            contractCode: contractCode,
            companyName: companyName,
            account: '',
            password: '',
        },
    });

    const [openManagementCode, setOpenManagementCode] = React.useState(false);
    const [openContractCode, setOpenContractCode] = React.useState(false);

    useEffect(() => {
        if (contractCode) {
            getContractCode(contractCode).then((res) => {
                if (res instanceof Error) {
                    return;
                }

                setValue('companyName', companyName);
            });
        }
        // eslint-disable-next-line
    }, []);

    const handleShowAlert = (alert: IAppAlert) => {
        dispatch(showAlert(alert));
    };

    const onSubmit = async (data: IFormInput) => {
        const body: IRequestLogin = {
            username: data.account,
            password: data.password,
            contract_code: data.contractCode || '',
        };

        const res = await loginFetcher(body);
        if (res instanceof Error) {
            const newAlert: IAppAlert = {
                status: true,
                type: ALERT_TYPE.ERROR,
                message: res.message,
            };
            dispatch(hideLoading());
            handleShowAlert(newAlert);

            console.error(`Get with error: ${res.message}`);
            return;
        }

        await changeI18nLocale(res.data.user.language);

        // Success
        dispatch(setUser(res.data.user));

        setLocalStorageValue(
            'authParams',
            JSON.stringify({
                accessToken: res.data.access_token,
                refreshToken: res.data.refresh_token,
                loginTime: `${Date.now()}`,
                user: res.data.user,
            }),
        );
    };

    const handleManagementCodeSubmit = (code: string) => {
        setOpenManagementCode(false);
        if (code === MANAGEMENT_CODE) {
            setOpenContractCode(true);
            return;
        }

        dispatch(
            showAlert({
                status: true,
                type: ALERT_TYPE.ERROR,
                message: t(TKeys.MSE00009),
            }),
        );
    };

    const handleOnPostContract = async (data: ContractCode) => {
        const res = await postContractCode(data);
        if (res.success) {
            const resData = res.data.data;
            setLocalStorageValue('contract', data.contract_code);
            const companyName = resData.company_name;
            setLocalStorageValue('companyName', companyName);
            setValue('companyName', companyName);
            setValue('contractCode', data.contract_code);

            dispatch(
                showAlert({
                    status: true,
                    type: ALERT_TYPE.SUCCESS,
                    message: res.message,
                }),
            );
        } else {
            dispatch(
                showAlert({
                    status: true,
                    type: ALERT_TYPE.ERROR,
                    message: res.error,
                }),
            );
        }
    };

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100vh"
            bgcolor="var(--color-bg-login)"
            position="relative">
            {/* Settings Icon */}
            <Box
                sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                }}>
                <IconButton
                    sx={{
                        background: 'transparent',
                        border: 0,
                        boxShadow: 'none',
                        ':hover': { background: 'transparent', boxShadow: 'none' },
                    }}
                    aria-label="Open Contract Settings"
                    // onClick={() => setOpenManagementCode(true)}>
                    // Maintain #15
                    onClick={() => setOpenContractCode(true)}>
                    <SettingsIcon fontSize="large" sx={{ cursor: 'pointer', color: 'black' }} />
                </IconButton>
            </Box>
            <Stack direction={'column'} spacing={'20px'} alignItems={'center'}>
                <Box
                    sx={{
                        width: '273px',
                        height: '80px',
                        margin: '0 auto',
                    }}>
                    <img
                        loading="lazy"
                        src="/banner-logo.png"
                        alt="logo"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block',
                            margin: '0 auto',
                        }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: '50px',
                        textAlign: 'center',
                        color: '#2281CC',
                    }}>
                    {APP_NAME}
                </Typography>
                {/* Login Form */}
                <Box>
                    <form onSubmit={handleSubmit(onSubmit)} noValidate className="login-form">
                        <Controller
                            inputType="TextField"
                            control={control}
                            name="contractCode"
                            label={t(TKeys.LBL020002)}
                            placeholder={'-'}
                            enableTrim
                            disabled={true}
                        />
                        <Controller
                            inputType="TextField"
                            control={control}
                            name="companyName"
                            label={t(TKeys.LBL020003)}
                            placeholder={'-'}
                            enableTrim
                            disabled={true}
                        />

                        <hr style={{ width: '100%', margin: '0', border: '1px solid #e0e0e0' }} />

                        <Controller
                            inputType="TextField"
                            control={control}
                            name="account"
                            rules={{ required: true }}
                            autoFocus
                            label={t(TKeys.LBL010020)}
                            placeholder={t(getMessageText(t(TKeys.MSE00001), t(TKeys.LBL010020)))}
                            maxLength={50}
                            enableTrim
                        />
                        <Controller
                            inputType="TextField"
                            control={control}
                            name="password"
                            type="password"
                            rules={{ required: true }}
                            autoFocus
                            label={t(TKeys.LBL010003)}
                            placeholder={t(getMessageText(t(TKeys.MSE00001), t(TKeys.LBL010003)))}
                            enableTrim
                        />
                        <Button
                            aria-label="Submit"
                            sx={{ fontSize: '16px' }}
                            type="submit"
                            title={t(TKeys.BTN010001)}
                        />
                    </form>
                </Box>
            </Stack>

            <ManagementCode
                open={openManagementCode}
                onClose={() => setOpenManagementCode(false)}
                onSubmit={handleManagementCodeSubmit}
            />

            <ContractCode
                open={openContractCode}
                onClose={() => setOpenContractCode(false)}
                onSubmit={handleOnPostContract}
            />
        </Box>
    );
};

export default LoginPage;
